import React, { useEffect, useState } from 'react';

// Material UI
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import { makeStyles } from 'tss-react/mui';

// Components
import Input from '../../../components/inputs/input';
import AutocompleteConcepts from '../../../components/inputs/autocompletes/AutocompleteConcepts';
import { utils } from '../../../utils/utils';
import { OrderStates } from '../../../models/OrderEntity';

const useStyles = makeStyles()(theme => ({
    success: {
        color: theme.palette.success.main
    }
}));

const RefundItem = ({ generalConcept, item, currency, onItemGeneralChange, onChange, onRemoveSelected, openMode }) => {
    const { classes } = useStyles();
    const [quantity, setQuantity] = useState(0);
    const [selected, setSelected] = useState(false);
    const [promotionsNames, setPromotionsNames] = useState("");
    const [concept, setConcept] = useState(generalConcept ?? null);

    const isTotal = openMode === OrderStates.Voided || openMode === OrderStates.InvoiceInitiated;
    item.refundedQuantity = item.refundedQuantity ?? 0;

    useEffect(() => {
        setQuantity(item.quantity - item.refundedQuantity);
        setSelected(item.selected);

        if (item.promotions?.length > 0) {
            const promotions = item.promotions.map(element => element.title).join(", ");
            setPromotionsNames(promotions);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setConcept(generalConcept)
        onItemGeneralChange(generalConcept);
    }, [generalConcept]);

    const onHandleSelected = async (event) => {
        setSelected(event.target.checked);

        if (event.target.checked)
            onChange(item.refId, item.warehouseId, quantity, concept);
        else
            onRemoveSelected(item.refId, item.warehouseId);
    };

    const handleInputChange = (event) => {
        const num = Number.parseInt(event.target.value);
        if (!isNaN(num) && (num > 0 & num <= (item.quantity - item.refundedQuantity))) {
            setQuantity(num);
            onChange(item.refId, item.warehouseId, num, concept);
        }
    }

    const handleAutocompleteChange = (event, options) => {
        setConcept(options);
        onChange(item.refId, item.warehouseId, quantity, options);
    }

    const getPromotionNames = () =>
        item.promotions
            .map(element => element.title)
            .join(", ")

    return (
        <Paper variant='outlined' style={{ padding: '8px 16px' }}>
            <Grid container spacing={2} alignItems='center'>
                {!isTotal &&
                    <>
                        <Grid size={1}>
                            <Checkbox
                                color="primary"
                                onChange={onHandleSelected}
                                checked={selected}
                                inputProps={{ 'aria-label': 'Devolver' }}
                            />
                        </Grid>
                        <Grid size={2}>
                            <Input
                                fullWidth
                                size='small'
                                value={quantity}
                                type='number'
                                onChange={handleInputChange}
                                label='Cantidad'
                                disabled={!selected}
                            />
                        </Grid>
                    </>
                }
                <Grid size="grow">
                    <Grid container>
                        <Grid size={12}>
                            <Typography component='span' variant='body1'>
                                <strong>{item.description}</strong>
                            </Typography>
                        </Grid>
                        <Grid size={5}>
                            <Typography variant='caption' display='block' color="textSecondary" gutterBottom>
                                {item.refId}
                            </Typography>
                            <Typography variant='body1' display='block'>
                                Sucursal: <strong>{item?.warehouseName}</strong>
                            </Typography>
                            {item.refundedQuantity > 0 &&
                                <Typography component='span' variant='body2' display='block' color='error'>
                                    {`Productos devueltos: ${item.refundedQuantity}`}
                                </Typography>
                            }
                        </Grid>
                        <Grid
                            style={{ placeSelf: 'flex-end' }}
                            size={{
                                xs: 7,
                                sm: "grow"
                            }}
                        >
                            <Typography variant='body2' display='block' align='right'>
                                {item.quantity} x {currency} <strong>{utils.formatMoney(item.unitPrice)}</strong>
                            </Typography>
                            {item.discount > 0 &&
                                <>
                                    <Typography variant='body2' display='block' align='right' className={classes.success}>
                                        {currency} <strong>{utils.formatMoney(item.discount)}</strong>
                                    </Typography>
                                    <Typography component='span' variant='overline' display='block' align='right'>
                                        <Typography component='span' variant='overline' style={{ textDecoration: 'line-through' }}>
                                            {currency} {utils.formatMoney(item.subAmount)}
                                        </Typography>
                                        <Typography component='span' variant='subtitle1' gutterBottom>
                                            {'\u00A0'} {currency} {utils.formatMoney(item.amount)}
                                        </Typography>
                                    </Typography>
                                </>
                            }
                            {item.discount <= 0 &&
                                <Typography component='span' variant='subtitle1' display='block' align='right'>
                                    {currency} {utils.formatMoney(item.amount)}
                                </Typography>
                            }
                        </Grid>
                        {promotionsNames !== "" &&
                            <Grid size={12}>
                                <Typography variant='body1' display='block'>
                                    Promocion: <strong>{getPromotionNames()}</strong>
                                </Typography>
                            </Grid>
                        }
                        {(openMode === OrderStates.Refunded || openMode === OrderStates.Voided || openMode === OrderStates.InvoiceInitiated) &&
                            <Grid style={{ marginTop: '10px' }} size={12}>
                                <Grid size={8}>
                                    <AutocompleteConcepts
                                        multiple={false}
                                        disabled={!selected && !isTotal}
                                        fullWidth
                                        limitTags={4}
                                        onChange={handleAutocompleteChange}
                                        value={concept}
                                        type={openMode === OrderStates.Refunded || openMode === OrderStates.Voided ? 'Void' : 'Change'}
                                    />
                                </Grid>
                            </Grid>
                        }
                    </Grid>
                </Grid>
            </Grid>
        </Paper>
    );
}

export default RefundItem;